import React, { useState, useEffect } from "react";
import { Card, Table, Button, Space, Typography, Empty, Spin, Popconfirm } from "antd";
import { PlusOutlined, DeleteOutlined, DesktopOutlined, EditOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { API_URL, getCredentials } from "../../shared/utils/Util";
import { getFetchT } from "../../shared/utils/TypedUtil";
import { useAntdTheme, useNotification } from "../../shared/hooks";
import { WorkstationSchema } from "./types";
import { WorkstationSchemaForm } from "./Form";

const { Text } = Typography;

type WorkstationSchemaEditorProps = {
  restaurantId: string;
};

export const WorkstationSchemaEditor: React.FC<WorkstationSchemaEditorProps> = ({
  restaurantId,
}) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { deleteSuccess, notifyError } = useNotification();
  const [schemas, setSchemas] = useState<WorkstationSchema[] | undefined>(undefined);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [editingSchema, setEditingSchema] = useState<WorkstationSchema | null>(null);

  const refreshSchemas = () => {
    getFetchT<{ workstation_schemas: WorkstationSchema[] }>(
      `${API_URL}/v1/r/${restaurantId}/admin/workstation_schemas`,
      (result) => setSchemas(result.workstation_schemas || []),
      (err) => {
        notifyError(t("failed_to_fetch_workstation_schemas"), err);
        setSchemas([]);
      }
    );
  };

  useEffect(() => {
    refreshSchemas();
  }, [restaurantId]);

  const handleDelete = (schemaId: string) => {
    setDeleting(schemaId);
    fetch(`${API_URL}/v1/r/${restaurantId}/admin/workstation_schemas/${schemaId}`, {
      credentials: "include",
      method: "DELETE",
      headers: {
        Authorization: `Basic ${getCredentials()}`,
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw res;
        }
        deleteSuccess(t("workstation_schema"));
        setSchemas(undefined);
        refreshSchemas();
      })
      .catch((err) => {
        err.text().then((errorMessage: string) => {
          notifyError(t("failed_to_delete_workstation_schema"), errorMessage);
        });
      })
      .finally(() => {
        setDeleting(null);
      });
  };

  const handleEdit = (schema: WorkstationSchema) => {
    setEditingSchema(schema);
  };

  const handleCancelEdit = () => {
    setEditingSchema(null);
  };

  const onChange = () => {
    setSchemas(undefined);
    setEditingSchema(null);
    refreshSchemas();
  };

  if (schemas === undefined) {
    return <Spin size="large" />;
  }

  const columns = [
    {
      title: t("name"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("prep_weight"),
      dataIndex: "prep_weight",
      key: "prep_weight",
      render: (value: number | undefined) => value ?? 0,
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_: unknown, record: WorkstationSchema) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            {t("edit")}
          </Button>
          <Popconfirm
            title={t("delete_workstation_schema_confirm")}
            description={t("delete_workstation_schema_description")}
            onConfirm={() => handleDelete(record.id)}
            okText={t("yes")}
            cancelText={t("no")}
          >
            <Button type="text" danger icon={<DeleteOutlined />} loading={deleting === record.id}>
              {t("delete")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={
          <Space>
            <PlusOutlined style={{ fontSize: 20, color: token.colorSuccess }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {editingSchema ? t("edit_workstation_schema") : t("add_workstation_schema")}
            </Text>
          </Space>
        }
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
          marginBottom: token.marginXL,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <WorkstationSchemaForm
          restaurantId={restaurantId}
          onSuccess={onChange}
          editingSchema={editingSchema}
          onCancelEdit={handleCancelEdit}
        />
      </Card>

      <Card
        title={
          <Space>
            <DesktopOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {t("current_workstation_schemas")}
            </Text>
          </Space>
        }
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        {schemas.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text type="secondary">{t("no_workstation_schemas_available")}</Text>}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={schemas}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `${t("total")} ${total} ${t("workstation_schemas")}`,
            }}
            style={{ borderRadius: token.borderRadiusLG }}
          />
        )}
      </Card>
    </div>
  );
};
